import { AgxFileUpload } from '@urbanx/agx-ui-components';
import {
  AgxBadge,
  AgxButton,
  AgxSpacer,
  Icon_EditOutline,
  useDialog,
} from '@urbanx/agx.ui-components2';
import type { UploadedDocument } from '@urbanx/agx.campaigns.client';
import { useCampaignFileUploadProps } from 'hooks/useCampaignFileUploadProps';
import { useAppSelector } from 'hooks/useAppSelector';
import ReplaceContractDialog from './ReplaceContractDialog';
import * as styles from './ContractReplacementUpload.css';

interface ContractReplacementUploadProps {
  id: string;
  label?: string | null;
  staffContract?: UploadedDocument | null;
  value?: UploadedDocument | null;
  required?: boolean;
  validate?: boolean;
  readonly?: boolean | null;
  onValueChanged: (change: { id: string; value: UploadedDocument | null }) => void;
}

const ContractReplacementUpload = ({
  id,
  label,
  staffContract,
  value,
  required,
  validate,
  readonly,
  onValueChanged,
}: ContractReplacementUploadProps) => {
  const { campaignId, formId } = useAppSelector((state) => state.form);
  const fileProps = useCampaignFileUploadProps({ contractId: formId });
  const replacementDialog = useDialog(ReplaceContractDialog, {
    hasExistingContract: !!staffContract,
  });
  const editReplacement = () => {
    if (readonly || !value || !campaignId || !formId) return;
    replacementDialog.open({
      campaignId,
      contractId: formId,
      uploadedDocument: value,
      onComplete: (document) => onValueChanged({ id, value: document }),
    });
  };

  return (
    <AgxSpacer direction="vertical" gap="spacing-md" fill>
      {staffContract && (
        <AgxFileUpload
          {...fileProps}
          id={`${id}-staff`}
          title={label ?? 'Contract'}
          fileData={[staffContract]}
          fileClassName={value ? styles.inactiveContract : undefined}
          fileBadge={
            <AgxBadge
              type="colourfill"
              colour="lightgrey"
              size="sm"
              text="Blank Contract"
              showBorder
            />
          }
          extensions={['pdf']}
          required={required}
          readonly
        />
      )}
      <AgxFileUpload
        {...fileProps}
        id={`${id}-replacement`}
        title={label ?? 'Contract'}
        fileData={value ? [value] : []}
        extensions={['pdf']}
        hideLabel={!!staffContract}
        required={required}
        validate={validate}
        errorMessage="Contract is required"
        readonly={!!readonly || !value}
        onValueChanged={({ value: files }) => {
          if (value && files.length === 0) onValueChanged({ id, value: null });
        }}
        fill
        customIcon={
          value && !readonly && campaignId && formId ? (
            <AgxButton
              id={`${id}-edit`}
              text=""
              ariaLabel="Edit replacement contract"
              onClick={editReplacement}
              leftIcon={<Icon_EditOutline />}
              variant="naked"
              size="small"
            />
          ) : undefined
        }
        onCustomIconClick={editReplacement}
      />
      {!value && !readonly && (
        <AgxButton
          id={`${id}-replace`}
          text={staffContract ? 'Replace Contract' : 'Choose file'}
          variant="hollow"
          size="medium"
          disabled={!campaignId || !formId}
          onClick={() => {
            if (!campaignId || !formId) return;
            replacementDialog.open({
              campaignId,
              contractId: formId,
              onComplete: (document) => onValueChanged({ id, value: document }),
            });
          }}
        />
      )}
    </AgxSpacer>
  );
};

export default ContractReplacementUpload;
